// ** Modules
import React from 'react';
import axios from 'axios';
import { useSession, type getProviders } from 'next-auth/react';

// ** UI
import useMediaQuery from '@mui/material/useMediaQuery';

// ** Components
import Loader from '@/components/common/Loader';
import Section from '@/components/common/Section';
import LoginRequiredPage from '@/features/LoginRequiredPage';

// ** Context
import { HeaderOverrideProvider } from '@/context/HeaderOverrideContext';

// ** Utils
import { getSubdomain } from '@/utils/domain/regions';
import { useGetFooter } from '@/lib/utils/footerDataUtils';
import { useGetNavigation } from '@/lib/utils/navigationDataUtils';

// ** Hooks
import { useSettings } from '@/hooks/useSettings';

// ** Layout
import Layout from '@/components/layout/Layout';
import VerticalAppBarContent from '@/components/layout/vertical/appBar/VerticalAppBarContent';
import HorizontalAppBarContent from '@/components/layout/horizontal/appBar/HorizontalAppBarContent';

// ** Types
import type { User } from 'next-auth';
import type { Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import type { AppContext, Settings } from '@/types/contextTypes';
import type { AuthentikGroupOption, AuthentikGroupResponse } from '@/types/authentik';
import type { MetaDataType, VerticalLayoutProps, HorizontalLayoutProps } from '@/types/layoutProps';

type ClientProviders = Awaited<ReturnType<typeof getProviders>>;
type UserLayoutProps = {
	host: AppContext['host'];
	locale: AppContext['locale'];
	metadata: MetaDataType;
	children: ReactNode;
	isPrivateRoute: boolean;
	providers?: ClientProviders;
	pageSettings?: Partial<Settings>;
};

const UserLayout = ({
	isPrivateRoute,
	children,
	locale,
	metadata = { title: 'Tischler NRW' },
	host,
	providers,
	pageSettings
}: UserLayoutProps) => {
	// Hooks
	const { data: session, status } = useSession();
	const { settings, saveSettings } = useSettings();

	// State
	const [authentikLoading, setAuthentikLoading] = React.useState(true);
	const [authentikGroups, setAuthentikGroups] = React.useState<Record<string, string>>({});

	// Fetch Authentik groups
	const fetchAuthentikGroups = React.useCallback(async () => {
		try {
			const response: AuthentikGroupResponse = await axios.get('/api/sso-groups');
			const groups: AuthentikGroupOption[] = (response.data as AuthentikGroupOption[]) || [];

			const groupMap: Record<string, string> = groups
				.filter((group: AuthentikGroupOption) =>
					['Ausschuss_', 'Funktion_', 'Position_', 'Tarif_', 'MSM_', '02 Ehrenamt', '02 Fachausschuss NRW'].some(
						(prefix) => group.name.startsWith(prefix)
					)
				)
				.reduce((acc: Record<string, string>, group: AuthentikGroupOption) => {
					acc[group.name] = group.pk;
					return acc;
				}, {});

			setAuthentikGroups(groupMap);
		} catch (error) {
			console.warn('⚠️ Authentik groups fetch failed:', error);
			setAuthentikGroups({});
		} finally {
			setAuthentikLoading(false);
		}
	}, []);

	React.useEffect(() => {
		fetchAuthentikGroups();
	}, [fetchAuthentikGroups]);

	React.useEffect(() => {
		if (pageSettings) {
			// Save the state before we change it
			const previousSettings = { ...settings };

			// Apply the page-specific settings
			saveSettings({ ...settings, ...pageSettings });

			// CLEANUP: Reset to previous state when navigating away
			return () => {
				saveSettings(previousSettings);
			};
		}
	}, [pageSettings, saveSettings, settings]);

	// Apollo - Queries
	const { data: navData } = useGetNavigation(locale, false);
	const { data: footerData } = useGetFooter(locale, false);

	// Vars
	const safeMetadata = metadata || { title: 'Tischler NRW' };
	const { title } = safeMetadata;
	const currentUser = session?.user || null;
	const userGroupIds = currentUser?.groups?.map((groupName: string) => authentikGroups[groupName]);

	const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

	// Navigation filtering
	const navItems = navData?.navigation?.navItems || [];
	const filterActiveNavItems = (items: any[], currentUser: User | null): any[] => {
		return items
			.filter((item) => {
				const isAuthenticated = item.authenticated ? !!currentUser : true;
				const restrictedToGroups = item.onlyVisibleToGroups ? JSON.parse(item.ssoGroups) : null;
				const hasAccess = !restrictedToGroups || restrictedToGroups.some((g: string) => userGroupIds?.includes(g));

				return item.active && isAuthenticated && hasAccess;
			})
			.map((item) => ({
				...item,
				children: item.children ? filterActiveNavItems(item.children, currentUser) : []
			}));
	};
	const filteredNavItems: any[] = filterActiveNavItems(navItems, currentUser);

	// Title update
	const newTitle = title !== 'Tischler NRW' ? `${title} | Tischler ${getSubdomain(host).toUpperCase()}` : title;
	const updatedMetadata = { ...metadata, title: newTitle };

	// Wait until session and Authentik data are loaded before rendering private page
	if (status === 'loading' || authentikLoading) {
		return <Loader />;
	}

	const safeFooterData = footerData?.footer || {};

	return (
		<HeaderOverrideProvider>
			<Layout
				providers={providers as any}
				currentUser={currentUser as User}
				locale={locale}
				metadata={updatedMetadata}
				host={host}
				hidden={hidden}
				settings={settings}
				saveSettings={saveSettings}
				footerProps={safeFooterData}
				{...(settings.layout === 'verticalNavigation' && {
					verticalLayoutProps: {
						navMenu: { navItems: filteredNavItems },
						appBar: {
							content: (props) => (
								<VerticalAppBarContent
									hidden={hidden}
									settings={settings}
									saveSettings={saveSettings}
									toggleNavVisibility={props.toggleNavVisibility}
									currentUser={currentUser as User}
									locale={locale}
									providers={providers as any}
									host={host}
								/>
							)
						} as VerticalLayoutProps['appBar']
					}
				})}
				{...(settings.layout === 'default' && {
					horizontalLayoutProps: {
						navMenu: {
							navItems: filteredNavItems
						},
						appBar: {
							content: (props) => (
								<HorizontalAppBarContent
									hidden={hidden}
									settings={settings}
									saveSettings={saveSettings}
									toggleNavVisibility={props.toggleNavVisibility}
									currentUser={currentUser as User}
									locale={locale}
									providers={providers as any}
									host={host}
									{...props}
								/>
							)
						} as HorizontalLayoutProps['appBar']
					}
				})}
			>
				{
					// Private page with no session
					isPrivateRoute && !currentUser ? (
						<Section
							containerSx={{
								minHeight: '83vh',
								alignContent: 'center',
								padding: 18
							}}
						>
							<LoginRequiredPage providers={providers} />
						</Section>
					) : React.isValidElement(children) ? (
						React.cloneElement(children as React.ReactElement<{ currentUser?: any }>, {
							currentUser
						})
					) : (
						children
					)
				}
			</Layout>
		</HeaderOverrideProvider>
	);
};

export default UserLayout;
